import styled from '@emotion/styled';
import { Mic, QueueMusicRounded, SettingsRounded } from '@mui/icons-material';
import { PropsWithChildren } from 'react';
import AdSlot from '~/modules/Ads/AdSlot';
import { AuthUserBadge } from '~/modules/Auth';
import ThemeToggle from '~/modules/Elements/ThemeToggle';
import useSmoothNavigate from '~/modules/hooks/useSmoothNavigate';
import { KaraokeBotPanel } from '~/modules/KaraokeBot';

const LIVE_TV_URL = 'https://fastlivetv.com';

export default function KaraokeAdLayout({ children }: PropsWithChildren) {
  const navigate = useSmoothNavigate();

  return (
    <Shell>
      <TopBar>
        <BrandGroup
          onClick={() => navigate('')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              navigate('');
            }
          }}
          aria-label="Ir al inicio de fastKaraoke">
          <BrandIcon src="/logo.svg" alt="" aria-hidden="true" />
          <BrandTitle>fastKaraoke</BrandTitle>
        </BrandGroup>

        <TopActions>
          <LiveTvLink href={LIVE_TV_URL} target="_blank" rel="noopener noreferrer" aria-label="Abrir liveTV">
            <LiveTvIcon src="/live-tv/logo.png" alt="" aria-hidden="true" />
            <span>liveTV</span>
          </LiveTvLink>

          <TopActionButton type="button" onClick={() => navigate('settings/')} aria-label="Settings">
            <SettingsRounded sx={{ fontSize: 18 }} />
            <span>Settings</span>
          </TopActionButton>

          <TopActionButton type="button" onClick={() => navigate('select-input/')} aria-label="Setup mics">
            <Mic sx={{ fontSize: 18 }} />
            <span>Setup mics</span>
          </TopActionButton>

          <TopActionButton type="button" onClick={() => navigate('jukebox/')} aria-label="Jukebox">
            <QueueMusicRounded sx={{ fontSize: 18 }} />
            <span>Jukebox</span>
          </TopActionButton>

          <AuthUserBadge />
          <HeaderThemeToggle />
        </TopActions>
      </TopBar>

      <Frame>
        <SideRail aria-label="Karaoke side rail">
          <ChatbotSlot data-chatbot-slot="karaoke" aria-label="KaraokeBot">
            <KaraokeBotPanel />
          </ChatbotSlot>
          <SideAdSlot>
            <AdSlot placement="karaoke-left-skyscraper" minHeight="clamp(120px, 26vh, 280px)" />
          </SideAdSlot>
        </SideRail>

        <MainColumn>
          <Header>
            <AdSlot placement="karaoke-top-banner" minHeight="100%" />
          </Header>
          <Content>
            {children}
            <MobileRail aria-label="Karaoke mobile ad rail">
              <SideAdSlot>
                <AdSlot placement="karaoke-mobile-bottom-banner" minHeight="clamp(96px, 28vw, 160px)" />
              </SideAdSlot>
              <ChatbotSlot data-chatbot-slot="karaoke-mobile" aria-label="KaraokeBot">
                <KaraokeBotPanel />
              </ChatbotSlot>
            </MobileRail>
          </Content>
        </MainColumn>
      </Frame>
    </Shell>
  );
}

const Shell = styled.div`
  --karaoke-top-bar-height: 54px;
  --karaoke-ad-header-height: clamp(60px, 9vh, 96px);
  --karaoke-ad-frame-padding-y: 20px;
  --karaoke-ad-header-gap: 12px;
  --karaoke-content-height: calc(
    100vh - var(--karaoke-top-bar-height) - var(--karaoke-ad-frame-padding-y) - var(--karaoke-ad-header-height) - var(
        --karaoke-ad-header-gap
      )
  );

  width: 100%;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
  background: var(--walky-canvas);
  color: var(--walky-ink);
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) {
    --karaoke-top-bar-height: 104px;
    --karaoke-ad-header-height: clamp(56px, 16vw, 76px);
    --karaoke-ad-frame-padding-y: 8px;
    --karaoke-ad-header-gap: 8px;
  }
`;

const TopBar = styled.header`
  flex: 0 0 var(--karaoke-top-bar-height);
  height: var(--karaoke-top-bar-height);
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--walky-border);
  background: var(--walky-surface-translucent, rgba(20, 24, 33, 0.95));
  backdrop-filter: blur(14px);
  z-index: 1000;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    align-content: center;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
  }
`;

const BrandGroup = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  flex: 1 1 auto;

  @media (max-width: 640px) {
    flex: 1 1 100%;
    gap: 8px;
  }
`;

const BrandIcon = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
  padding: 3px;
  box-sizing: border-box;
  border-radius: 8px;
  background: var(--walky-logo-surface, #ffffff);
  border: 1px solid var(--walky-border);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const BrandTitle = styled.h1`
  min-width: 0;
  margin: 0;
  color: var(--walky-ink);
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 420px) {
    font-size: 18px;
  }
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 680px) {
    gap: 6px;
  }

  @media (max-width: 640px) {
    width: 100%;
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;
    flex: 1 1 100%;

    > * {
      min-width: 0;
    }
  }
`;

const TopActionButton = styled.button`
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  background: var(--walky-surface-raised);
  color: var(--walky-ink);
  box-shadow: var(--walky-shadow);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  transition: all 160ms ease;

  &:hover,
  &:focus-visible {
    color: white;
    background: var(--walky-orange);
    border-color: var(--walky-orange);
    transform: translateY(-1px);
    outline: none;
  }

  @media (max-width: 720px) {
    padding: 0 8px;
    span {
      display: none;
    }
  }

  @media (max-width: 640px) {
    width: 100%;
    height: 38px;
    padding: 0;
  }
`;

const LiveTvLink = styled.a`
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  border: 1px solid rgba(244, 90, 10, 0.45);
  border-radius: 8px;
  background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(244, 90, 10, 0.2);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  transition: all 160ms ease;

  &:hover,
  &:focus-visible {
    filter: brightness(1.08);
    transform: translateY(-1px);
    outline: none;
  }

  @media (max-width: 640px) {
    width: 100%;
    height: 38px;
    padding: 0;

    span {
      display: none;
    }
  }
`;

const LiveTvIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #ffffff;
  padding: 1px;
  box-sizing: border-box;
`;

const HeaderThemeToggle = styled(ThemeToggle)`
  @media (max-width: 640px) {
    width: 100%;
    height: 38px;
    min-width: 0;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Frame = styled.div`
  width: min(100%, 1480px);
  flex: 1 1 auto;
  height: calc(100vh - var(--karaoke-top-bar-height));
  min-height: 0;
  margin: 0 auto;
  padding: 10px 16px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 920px) {
    display: flex;
    flex-direction: column;
    height: calc(100vh - var(--karaoke-top-bar-height));
    overflow: hidden;
  }

  @media (max-width: 640px) {
    gap: 8px;
    padding: 8px 10px;
  }
`;

const SideRail = styled.aside`
  /* Above the fixed blurred song background rendered by the main column */
  position: relative;
  z-index: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;

  @media (max-width: 920px) {
    display: none;
  }
`;

const MainColumn = styled.section`
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 920px) {
    order: 1;
    width: 100%;
    height: 100%;
    overflow: hidden;
    flex: 0 0 auto;
  }
`;

const Header = styled.header`
  flex: 0 0 var(--karaoke-ad-header-height);
  height: var(--karaoke-ad-header-height);
  box-sizing: border-box;
  margin-bottom: var(--karaoke-ad-header-gap);
`;

const Content = styled.div`
  flex: 1 1 auto;
  height: var(--karaoke-content-height);
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;

  @media (max-width: 920px) {
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

const SideAdSlot = styled.div`
  min-width: 0;
`;

const MobileRail = styled.aside`
  display: none;

  @media (max-width: 920px) {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin-top: 16px;
    padding-bottom: 16px;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ChatbotSlot = styled.div`
  flex: 1 1 auto;
  min-height: 420px;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--walky-border);
  border-top: 3px solid var(--walky-orange);
  border-radius: 8px;
  background: var(--walky-surface);
  color: var(--walky-ink);
  box-shadow: var(--walky-shadow);
  display: flex;

  > * {
    min-width: 0;
    flex: 1 1 auto;
  }

  @media (max-width: 920px) {
    order: 2;
    min-height: clamp(300px, 72vw, 380px);
  }
`;
